import React, {
  Suspense,
  lazy,
  ComponentType,
  useState,
  useEffect,
} from "react";
import { PageWrapper } from "../Base/PageWrapper/PageWrapper";
import { KeepAlive } from "react-activation";
import { useUserStore } from "../../modules/user/stores";
import PageLoading from "../PageLoading";

const DefaultLoadingFallback = <PageLoading init={true} />;

export function withLazyLoading<T extends Record<string, any>>(
  importFunc: () => Promise<{ default: ComponentType<T> }>,
  fallback: React.ReactNode = DefaultLoadingFallback
) {
  const LazyComponent = lazy(importFunc);

  return function LazyWrapper(props: T) {
    return (
      <Suspense fallback={fallback}>
        <LazyComponent {...(props as any)} />
      </Suspense>
    );
  };
}

export function withLazyLoadingAndPageWrapper<T extends Record<string, any>>(
  importFunc: () => Promise<{ default: ComponentType<T> }>,
  fallback: React.ReactNode = DefaultLoadingFallback
) {
  const LazyComponent = lazy(importFunc);

  return function LazyWrapperWithPageWrapper(props: T) {
    return (
      <Suspense fallback={fallback}>
        <PageWrapper>
          <LazyComponent {...(props as any)} />
        </PageWrapper>
      </Suspense>
    );
  };
}

// HomePage 是首屏特殊路由：fallback 显式传 null，不渲染 <PageLoading init>。
// 原因：HomePage chunk 加载期间，index.html 的 #app-loading 仍持续显示
// （MainShell.Entry 已不再提前 hide），#root opacity 还是 0，看不见 React
// 内容，无需 fallback 占位。如果用 PageLoading 占位，#root 渐显时会与
// #app-loading 渐隐 crossfade 出"两次 loading"的视觉抖动。
// 其他 lazy 页面（VIP / Profile / EventCenter 等）用户已经在 loaded 状态下
// 才点击进入，需要 PageLoading 占位，所以默认 fallback 仍保留。
export const LazyHomePage = withLazyLoading(
  () => import("../../pages/HomePage"),
  null
);

export const KeepAliveHomePage = () => {
  const { userInfo } = useUserStore();
  const newCacheKey = `HomePage-${userInfo?.userId || "guest"}`;
  const [cacheKey, setCacheKey] = useState(newCacheKey);

  useEffect(() => {
    if (newCacheKey !== cacheKey) {
      setCacheKey(newCacheKey);
    }
  }, [newCacheKey]);

  return (
    <KeepAlive name="HomePage" id="HomePage" cacheKey={cacheKey}>
      <LazyHomePage />
    </KeepAlive>
  );
};

export const LazyEventCenterPage = withLazyLoadingAndPageWrapper(
  () => import("../../pages/EventCenter")
);
export const KeepAliveEventCenterPage = () => {
  const { userInfo } = useUserStore();
  const cacheKey = `EventCenterPage-${userInfo?.userId || "guest"}`;

  return (
    <KeepAlive
      name="EventCenterPage"
      id="EventCenterPage"
      cacheKey={cacheKey}
      autoFreeze={false}
    >
      <LazyEventCenterPage />
    </KeepAlive>
  );
};

export const LazyAgentCenterPage = withLazyLoadingAndPageWrapper(
  () => import("../../pages/AgentCenter")
);
export const KeepAliveAgentCenterPage = () => {
  const { userInfo } = useUserStore();
  const cacheKey = `AgentCenterPage-${userInfo?.userId || "guest"}`;

  return (
    <KeepAlive
      name="AgentCenterPage"
      id="AgentCenterPage"
      cacheKey={cacheKey}
      autoFreeze={false}
    >
      <LazyAgentCenterPage />
    </KeepAlive>
  );
};

export const LazyInviteFriendsPage = withLazyLoadingAndPageWrapper(
  () => import("../../pages/InviteFriends")
);
export const KeepAliveInviteFriendsPage = () => {
  return <LazyInviteFriendsPage />;
};

export const LazyVIPPage = withLazyLoadingAndPageWrapper(
  () => import("../../pages/VIP")
);
export const KeepAliveVIPPage = () => {
  const { userInfo } = useUserStore();
  const vipLevel = userInfo?.vipLevel?.level || 0;
  const cacheKey = `VIPPage-${userInfo?.userId || "guest"}-${vipLevel}`;

  return (
    <KeepAlive
      name="VIPPage"
      id="VIPPage"
      cacheKey={cacheKey}
      autoFreeze={false}
    >
      <LazyVIPPage />
    </KeepAlive>
  );
};

export const LazyProfilePage = withLazyLoadingAndPageWrapper(
  () => import("../../pages/Profile")
);
export const KeepAliveProfilePage = () => {
  const { userInfo } = useUserStore();
  const cacheKey = `ProfilePage-${userInfo?.userId || "guest"}`;

  return (
    <KeepAlive
      name="ProfilePage"
      id="ProfilePage"
      cacheKey={cacheKey}
      autoFreeze={false}
    >
      <LazyProfilePage />
    </KeepAlive>
  );
};

export function createLazyRoute(
  importFunc: () => Promise<{ default: ComponentType<any> }>
) {
  return withLazyLoading(importFunc);
}

export function createLazyRouteWithPageWrapper(
  importFunc: () => Promise<{ default: ComponentType<any> }>
) {
  return withLazyLoadingAndPageWrapper(importFunc);
}
